/**
 * TikTok 发品前平台核验：
 * - Check Listing Prerequisites（店铺仓/税/运费模板等）
 * - Check Product Listing（与 Create Product 同载荷）
 */

import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { tiktokPlatformAPI } from '@/utils/api';
import { isTiktokTtsPublishMode } from '@/tiktok-platform/utils/tiktokPublishUiMode';

function fail(message, section = 'basic', field = 'form') {
  return { ok: false, message: fixedUi(message), section, field };
}

function asText(v) {
  return String(v ?? '').trim();
}

function unwrapData(res) {
  if (!res || typeof res !== 'object') return {};
  const response = res.response && typeof res.response === 'object' ? res.response : null;
  const nested = response?.data && typeof response.data === 'object' ? response.data : null;
  if (nested) return nested;
  if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) return res.data;
  return res;
}

/** 平台 fail_reasons / pre_check / 文案 → 站内字段 */
export function mapListingCheckMessageToField(message = '', relatedFields = []) {
  const text = `${asText(message)} ${relatedFields.map(asText).join(' ')}`.toLowerCase();
  if (/title|标题|商品名|pre_check_field_title/.test(text)) {
    return { section: 'basic', field: 'title' };
  }
  if (/description|描述|pre_check_field_desc/.test(text)) {
    return { section: 'basic', field: 'description' };
  }
  if (/categor|类目/.test(text)) {
    return { section: 'basic', field: 'categoryId' };
  }
  if (/brand|品牌/.test(text)) {
    return { section: 'basic', field: 'brandCode' };
  }
  if (/image|图片|主图|gallery|pre_check_field_image/.test(text)) {
    return { section: 'spec', field: 'skcList' };
  }
  if (/attribute|属性|pre_check_field_attr/.test(text)) {
    return { section: 'attrs', field: 'productAttributes' };
  }
  if (/size.?chart|尺码/.test(text)) {
    return { section: 'spec', field: 'sizeChart' };
  }
  if (/warehouse|仓库|发货仓|pickup|return_warehouse/.test(text)) {
    return { section: 'sales', field: 'warehouseId' };
  }
  if (/shipping.?template|运费|物流模板|freight/.test(text)) {
    return { section: 'sales', field: 'freightTemplateId' };
  }
  if (/package|重量|体积|weight|dimension/.test(text)) {
    return { section: 'size', field: 'supply' };
  }
  if (/sku|库存|库存|price|价格|seller_sku/.test(text)) {
    return { section: 'spec', field: 'supply' };
  }
  return { section: 'basic', field: 'form' };
}

export function parseListingPrerequisitesResult(res) {
  const data = unwrapData(res);
  const rows = Array.isArray(res?.checkResults)
    ? res.checkResults
    : (Array.isArray(data.check_results) ? data.check_results : []);
  const failed = rows.filter((row) => row && (row.is_failed === true || row.isFailed === true));
  if (!failed.length) return { ok: true, failed: [] };
  const messages = failed.flatMap((row) => {
    const reasons = Array.isArray(row.fail_reasons)
      ? row.fail_reasons
      : (Array.isArray(row.failReasons) ? row.failReasons : []);
    const item = asText(row.check_item || row.checkItem);
    if (reasons.length) {
      return reasons.map((r) => (
        item ? fixedUiTpl('【{0}】{1}', item, asText(r)) : asText(r)
      )).filter(Boolean);
    }
    return item ? [fixedUiTpl('店铺前置条件未通过：{0}', item)] : [];
  }).filter(Boolean);
  const first = messages[0] || fixedUi('店铺发品前置条件未通过，请先在 Seller Center 完成配置');
  const mapped = mapListingCheckMessageToField(first);
  return {
    ok: false,
    message: messages.length > 1
      ? fixedUiTpl('{0}（另有 {1} 项）', first, messages.length - 1)
      : first,
    messages,
    ...mapped,
  };
}

export function parseListingCheckResult(res) {
  const data = unwrapData(res);
  const checkResult = asText(res?.checkResult || data.check_result || data.checkResult).toUpperCase();
  const failReasons = Array.isArray(res?.failReasons)
    ? res.failReasons
    : (Array.isArray(data.fail_reasons) ? data.fail_reasons : []);
  const preChecks = Array.isArray(res?.preCheckResults)
    ? res.preCheckResults
    : (Array.isArray(data.pre_check_results) ? data.pre_check_results : []);

  const messages = [];
  failReasons.forEach((row) => {
    const msg = asText(row?.message || row?.msg);
    const code = row?.code != null ? String(row.code) : '';
    if (msg) messages.push(code ? fixedUiTpl('{0}（{1}）', msg, code) : msg);
  });
  preChecks.forEach((row) => {
    const details = Array.isArray(row?.pre_check_details)
      ? row.pre_check_details
      : (Array.isArray(row?.preCheckDetails) ? row.preCheckDetails : []);
    details.forEach((d) => {
      const longReason = asText(d?.long_reason || d?.longReason);
      const shortReason = asText(d?.short_reason || d?.shortReason);
      const msg = longReason || shortReason;
      if (msg) messages.push(msg);
    });
    if (!details.length) {
      const item = asText(row?.pre_check_item || row?.preCheckItem);
      if (item) messages.push(fixedUiTpl('商品信息核验未通过：{0}', item));
    }
  });

  const warningMsg = asText(
    res?.warnings?.message
    || data?.warnings?.message
    || (typeof data?.warnings === 'string' ? data.warnings : ''),
  );

  if (checkResult === 'PASS' || (!checkResult && !messages.length)) {
    return {
      ok: true,
      checkResult: checkResult || 'PASS',
      warning: warningMsg || '',
      messages: [],
    };
  }

  // 无显式 PASS 且有失败明细，或明确 FAILED
  if (checkResult === 'FAILED' || messages.length) {
    const first = messages[0] || fixedUi('平台发品核验未通过，请按提示修改后重试');
    const related = [];
    preChecks.forEach((row) => {
      const details = Array.isArray(row?.pre_check_details)
        ? row.pre_check_details
        : (Array.isArray(row?.preCheckDetails) ? row.preCheckDetails : []);
      details.forEach((d) => {
        const fields = Array.isArray(d?.related_fields) ? d.related_fields : (d?.relatedFields || []);
        related.push(...fields);
      });
    });
    const mapped = mapListingCheckMessageToField(first, related);
    return {
      ok: false,
      checkResult: checkResult || 'FAILED',
      message: messages.length > 1
        ? fixedUiTpl('{0}（另有 {1} 项）', first, messages.length - 1)
        : first,
      messages,
      warning: warningMsg || '',
      ...mapped,
    };
  }

  return { ok: true, checkResult: checkResult || 'PASS', warning: warningMsg || '', messages: [] };
}

/**
 * 编辑 / 改价提交前核验：只跑 Check Listing Prerequisites。
 *
 * 编辑场景**不应**跑 Check Product Listing——它是 Create Product 的同载荷校验，
 * 对已存在商品会报重复标题/货号（误拦）；店铺级前置条件（仓/税/运费模板/权限）
 * 才是编辑能提交的真实门槛。
 */
export async function runTiktokEditPrerequisitesCheck({ shopId } = {}) {
  if (!isTiktokTtsPublishMode()) {
    return { ok: true, skipped: true };
  }
  if (!shopId) return fail('请先选择店铺');
  try {
    const res = await tiktokPlatformAPI.checkListingPrerequisites(shopId);
    return parseListingPrerequisitesResult(res);
  } catch (e) {
    const msg = asText(e?.message || e?.userMessage);
    // 明确业务失败（权限/仓/税/运费）拦截；网络抖动不阻断编辑
    if (/前置|warehouse|tax|shipping|权限|未授权|403|401/i.test(msg)) {
      return fail(msg || '店铺发品前置条件核验失败', 'sales', 'form');
    }
    return { ok: true, failed: [], warning: msg || '' };
  }
}

/** Check Product Listing 未收录的 Create Product 字段（仅提交时使用） */
const LISTING_CHECK_STRIPPED_FIELDS = ['purchase_order_quantity_limit', 'idempotency_key'];

/**
 * 浅拷贝并剔除 listing_check 未收录字段；不修改调用方对象（提交仍需完整字段）。
 */
function stripListingCheckOnlyFields(body) {
  if (!body || typeof body !== 'object') return body;
  const source = body.ttsProduct && typeof body.ttsProduct === 'object'
    ? body.ttsProduct
    : body;
  const hasStripped = LISTING_CHECK_STRIPPED_FIELDS.some((key) => source[key] != null);
  if (!hasStripped) return body;
  const sanitized = { ...source };
  LISTING_CHECK_STRIPPED_FIELDS.forEach((key) => {
    delete sanitized[key];
  });
  return body.ttsProduct && typeof body.ttsProduct === 'object'
    ? { ...body, ttsProduct: sanitized }
    : sanitized;
}

/**
 * 发品提交前：店铺前置条件 + listing_check。
 * @param {{ shopId: string|number, ttsProduct?: object, payload?: object }} args
 *   payload 可为含 ttsProduct 的包装体；优先用 ttsProduct 原生体。
 */
export async function runTiktokListingPlatformCheck({
  shopId,
  ttsProduct = null,
  payload = null,
  skipPrerequisites = false,
} = {}) {
  if (!isTiktokTtsPublishMode()) {
    return { ok: true, skipped: true };
  }
  if (!shopId) return fail('请先选择店铺');

  if (!skipPrerequisites) {
    try {
      const prereqRes = await tiktokPlatformAPI.checkListingPrerequisites(shopId);
      const prereq = parseListingPrerequisitesResult(prereqRes);
      if (!prereq.ok) return prereq;
    } catch (e) {
      // 前置条件接口偶发失败时不阻断（仍走 listing_check）；明确业务失败除外
      const msg = asText(e?.message || e?.userMessage);
      if (/前置|warehouse|tax|shipping|权限|未授权|403|401/i.test(msg)) {
        return fail(msg || '店铺发品前置条件核验失败', 'sales', 'form');
      }
    }
  }

  const rawBody = ttsProduct && typeof ttsProduct === 'object'
    ? { ttsProduct }
    : (payload && typeof payload === 'object' ? payload : null);
  if (!rawBody) return fail('缺少发品参数，无法调用平台核验');
  // Check Product Listing 未收录 Create Product 的限购 / 幂等键；浅拷贝剔除，
  // 避免平台因未知字段报错（同时不改动调用方用于提交的 payload）。
  const body = stripListingCheckOnlyFields(rawBody);

  try {
    const res = await tiktokPlatformAPI.checkProductListing(shopId, body, {
      diagnosisRequired: false,
    });
    return parseListingCheckResult(res);
  } catch (e) {
    return fail(
      asText(e?.message || e?.userMessage) || '平台发品核验失败，请稍后重试',
      'basic',
      'form',
    );
  }
}
